import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { CartLineCollectionModel, CartLineModel, ProductInventoryDto } from "@insite/client-framework/Types/ApiModels";
import { getVendorStockStatus, isHazardousProduct } from "@MasterBluePrint/Tracking/AddToCart/Utils/addToCartUtils";
import { getPageSource } from "@MasterBluePrint/Tracking/trackingHelper";

export interface AddToCartCollectionPayload {
    event: "addToCart";
    ecommerce: {
        currencyCode?: string;
        add: {
            actionField: { id?: string; list: string; action: "add"; };
            products: Array<{
                name: string;
                id: string;
                price?: number;
                brand?: string;
                condition?: string;
                quantity: number | null;
                stock?: string;
                category?: string;
                position?: number | null;
                vendorStockQty?: number | "";
                vendorStockStatus?: string | "";
                Hazardous?: "Yes" | "";
            }>;
        };
    };
}

const buildAddToCartCollectionTracking = (
    products: CartLineCollectionModel,
    state: ApplicationState,
    currentUrl: string,
    productInventoryDtos?: ProductInventoryDto[],
): AddToCartCollectionPayload => {
    let wishListIdWhenShared: string | undefined;
    const pageSourceResult = getPageSource(currentUrl, "Product Details");

    if (pageSourceResult === "Shared Cart") {
        const wishList = getWishListState(state, state.pages.staticList.wishListId).value;
        wishListIdWhenShared = wishList?.id;
    }

    const dataLayerProducts: Array<AddToCartCollectionPayload["ecommerce"]["add"]["products"][number]> = [];
    products?.cartLines?.forEach((cartLine: CartLineModel) => {
        let productCondition = "";
        let vendorStockQty: number | undefined;
        let vendorStockStatus: string | undefined;
        let brandName = "";

        if (cartLine.productId) {
            const productData = getProductState(state, cartLine.productId).value;
            productCondition = productData?.properties?.condition || cartLine?.properties?.cartLineCondition || "";
            const matchedProductInventoryDto = productInventoryDtos?.find(x => x.productId === cartLine.productId);
            const productInventoryDtoList = matchedProductInventoryDto ? [matchedProductInventoryDto] : [];
            const vendorStockInfo = getVendorStockStatus(cartLine, {} as ProductInfo, productInventoryDtoList);
            vendorStockQty = vendorStockInfo.vendorStockQty;
            vendorStockStatus = vendorStockInfo.vendorStockStatus;
            const clProps = cartLine.properties;
            brandName = cartLine?.properties.cartlineBrandName || clProps.cartLineBrandName || "";
        }

        const hazardousProduct = isHazardousProduct(cartLine);

        dataLayerProducts.push({
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            price: cartLine.pricing?.actualPrice,
            brand: brandName,
            condition: productCondition,
            quantity: cartLine.qtyOrdered,
            category: cartLine.properties?.cartlineCategoryName,
            position: cartLine?.line,
            stock: cartLine?.availability?.message,
            vendorStockQty,
            vendorStockStatus,
            Hazardous: hazardousProduct,
        });
    });

    const currencyCode = state.context.session.currency?.currencyCode;

    const actionField: { id?: string; list: string; action: "add" } =
        pageSourceResult === "Shared Cart"
            ? { id: wishListIdWhenShared, list: pageSourceResult, action: "add" }
            : { list: pageSourceResult, action: "add" };

    return {
        event: "addToCart",
        ecommerce: {
            currencyCode,
            add: {
                actionField,
                products: dataLayerProducts,
            },
        },
    };
}

export default buildAddToCartCollectionTracking;